
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { SECRET_TO_PLATFORM_MAP } from '../constants';
import { toast } from "sonner";

export const useIntegrationStatus = () => {
  const [connectedPlatforms, setConnectedPlatforms] = useState<Record<string, boolean>>({});
  const [secretsConfigured, setSecretsConfigured] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsLoading(false);
        return;
      }
      
      // Check Supabase Secrets first (prioritized)
      const { data: secretsData, error: secretsError } = await supabase.functions.invoke('check-secrets-status', {
        body: { secrets: Object.keys(SECRET_TO_PLATFORM_MAP) }
      });
      
      const secretsStatus: Record<string, boolean> = {};
      if (secretsError) {
        console.error('Error checking secrets:', secretsError);
      } else if (secretsData) {
        Object.entries(secretsData).forEach(([secret, exists]) => {
          const platformId = SECRET_TO_PLATFORM_MAP[secret];
          if (platformId && exists) {
            secretsStatus[platformId] = true;
          }
        });
        setSecretsConfigured(secretsStatus);
      }

      // Check database credentials as fallback
      const { data: credentialsData, error } = await supabase
        .from('platform_credentials')
        .select('platform, is_active');
      
      if (error) throw error;
      
      const platformStatus: Record<string, boolean> = {};
      if (credentialsData) {
        credentialsData.forEach(item => {
          // Only mark as connected if not already handled by secrets
          if (!secretsStatus[item.platform]) {
            platformStatus[item.platform] = item.is_active;
          }
        });
      }
      
      setConnectedPlatforms(platformStatus);
    } catch (error) {
      console.error('Error loading connection status:', error);
      toast.error('Failed to load integration status');
    } finally {
      setIsLoading(false);
    }
  };

  const testConnection = async (platformId: string) => {
    try {
      setTestingConnection(platformId);
      
      // Test connection using the edge function (which prioritizes Supabase Secrets)
      const { data, error } = await supabase.functions.invoke('test-connection', {
        body: { platform: platformId }
      });
      
      if (error) {
        console.error(`Connection test error for ${platformId}:`, error);
        toast.error(`Connection test failed: ${error.message}`);
        return;
      }
      
      if (data?.success) {
        toast.success(`${platformId.toUpperCase()} connection successful!`);
        
        // Update the status to reflect successful connection
        if (secretsConfigured[platformId]) {
          // Already handled by secrets
        } else {
          setConnectedPlatforms(prev => ({
            ...prev,
            [platformId]: true
          }));
        }
      } else {
        toast.error(`${platformId.toUpperCase()} connection failed: ${data?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(`Error testing connection for ${platformId}:`, error);
      toast.error(`Failed to test connection: ${(error as Error).message}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const isPlatformConnected = (platformId: string): boolean => {
    // Prioritize Supabase Secrets over database credentials
    return secretsConfigured[platformId] === true || connectedPlatforms[platformId] === true;
  };

  const getPlatformStatus = (platformId: string): 'connected' | 'idle' | 'error' => {
    if (isPlatformConnected(platformId)) {
      return 'connected';
    }
    return 'idle';
  };

  const getConnectionSource = (platformId: string): 'secrets' | 'database' | 'none' => {
    if (secretsConfigured[platformId]) {
      return 'secrets';
    }
    if (connectedPlatforms[platformId]) {
      return 'database';
    }
    return 'none';
  };

  return {
    isLoading,
    testingConnection,
    secretsConfigured,
    connectedPlatforms,
    testConnection,
    getPlatformStatus,
    isPlatformConnected,
    getConnectionSource,
    refreshStatus: loadConnectionStatus
  };
};
